// ShareButton — "Copy share link" affordance in the results header (#244).
//
// A small text+icon link (not a primary button) so it reads as a secondary
// action beside "See the impact" — it deliberately does NOT live in the Reset
// all / Simulate action bar. Issue #65's "your reform" modal is its eventual
// home; this header seat pre-positions that.
//
// Subscribes to the share registry (share_store) so it enables the instant any
// control becomes modified, and recomputes the link as controls change. The
// computed URL is exposed on data-share-url so tests can assert it without
// depending on the flaky headless-clipboard path.
//
// Clipboard write degrades gracefully: navigator.clipboard needs a secure
// context (HTTPS / localhost) and can reject, so a hidden-textarea + execCommand
// fallback covers http previews and older engines.

import { useMemo, useState } from "react";
import { anyModified, buildShareUrl, useShareStore } from "../../state/share_store";

// Bootstrap Icons "share" (MIT), inheriting currentColor — mirrors InfoIcon.
function ShareIcon(): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5" />
    </svg>
  );
}

async function copyText(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    // fall through to the legacy path
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(ta);
  }
}

export function ShareButton(): JSX.Element {
  const controls = useShareStore((s) => s.controls);
  const enabled = anyModified(controls);
  const url = useMemo(() => buildShareUrl(controls), [controls]);
  const [copied, setCopied] = useState(false);

  const onClick = (): void => {
    void copyText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      type="button"
      className="beamm-share-link"
      data-testid="share-link"
      data-share-url={url}
      disabled={!enabled}
      aria-label="Copy a shareable link to this reform"
      onClick={onClick}
    >
      <ShareIcon />
      <span>{copied ? "Link copied" : "Copy share link"}</span>
    </button>
  );
}
